import { gql } from '@apollo/client';
import { TRANSLATED_STRING_FIELDS } from '@assets/i18n/config';

const RULE_GROUP_FIELDS = `
  combinator
  customFieldKey
  customFieldType
  field
  objectType
  operator
  value
`;

export const CONDITION = `#graphql
   condition {
     ruleGroup {
       ${RULE_GROUP_FIELDS}
       rules {
         ${RULE_GROUP_FIELDS}
         rules {
           ${RULE_GROUP_FIELDS}
           rules {
             ${RULE_GROUP_FIELDS}
             rules {
               ${RULE_GROUP_FIELDS}
             }
           }
         }
       }
     }
   }
 `;

export const PAGE_CONFIGURATION = (isConditionEnabled = false) => `#graphql
  pageConfiguration {
    id
    name
    type
    options
    tabs {
      id
      icon
      translatedUrl { ${TRANSLATED_STRING_FIELDS} }
      label { ${TRANSLATED_STRING_FIELDS} }
      contextObject
      workflow { id state objectType }
      widgets {
        id
        type
        options
        constraints
        contextObject
        ${isConditionEnabled ? CONDITION : ''}
      }
      ${isConditionEnabled ? CONDITION : ''}
    }
    publicOrganization { id }
  }
`;

export const FETCH_PAGE_CONFIGURATION = (isConditionEnabled = false) => gql`
  query fetchPageConfiguration($pageConfigurationId: ObjectID!) {
    fetchPageConfiguration (pageConfigurationId: $pageConfigurationId) {
      ${PAGE_CONFIGURATION(isConditionEnabled)}
    }
  }
`;

export const UPDATE_PAGE_CONFIGURATION = gql`
  mutation updatePageConfiguration($input: UpdatePageConfigurationInput!) {
    updatePageConfiguration(input: $input) {
      ${PAGE_CONFIGURATION()}
    }
  }
`;

export const FETCH_RELATED_OBJECTS = gql`
  query fetchUser($userId: SafeString!) {
    fetchUser(input: { userId: $userId }) {
      user {
        id: stringId
        firstName
        middleName
        lastName
        entityName
        goals {
          id
        }
        accounts {
          id: stringId
        }
        subAccounts {
          id
        }
        accountGroups {
          id
        }
        households {
          id: stringId
          statistics {
            marketValueCents
          }
          relationships {
            type
            user {
              id: stringId
            }
          }
        }
        statistics {
          marketValueCents
        }
        organization {
          id
          displayCurrency
          availableFeatureFlags
        }
      }
    }
  }
`;

export const TRANSITION_PAGE_CONFIGURATION = gql`
  mutation transitionPageConfiguration(
    $input: TransitionPageConfigurationInput!
  ) {
    transitionPageConfiguration(input: $input) {
      pageConfiguration {
        id
        state
      }
    }
  }
`;

// Reusable user fields for reverse lookup queries
const USER_WITH_RELATED_OBJECTS = `
  id: stringId
  firstName middleName lastName entityName
  goals { id }
  accounts { id: stringId }
  subAccounts { id }
  accountGroups { id }
  households {
    id: stringId
    statistics { marketValueCents }
    relationships{ type user { id: stringId } }
  }
  statistics { marketValueCents }
  organization { id displayCurrency availableFeatureFlags }
`;

// Reverse lookup queries - fetch user from objectId
export const FETCH_ACCOUNT_WITH_USER = gql`
  query fetchAccountWithUser($accountId: SafeString!) {
    fetchAccount(input: { accountId: $accountId }) {
      account {
        id: stringId
        user {
          ${USER_WITH_RELATED_OBJECTS}
        }
      }
    }
  }
`;

export const FETCH_GOAL_WITH_USER = gql`
  query fetchGoalWithUser($goalId: ObjectID!) {
    fetchGoal(goalId: $goalId) {
      goal {
        id
        user {
          ${USER_WITH_RELATED_OBJECTS}
        }
      }
    }
  }
`;

export const FETCH_SUB_ACCOUNT_WITH_USER = gql`
  query fetchSubAccountWithUser($subAccountId: ObjectID!) {
    fetchSubAccount(subAccountId: $subAccountId) {
      subAccount {
        id
        account {
          id: stringId
          user {
            ${USER_WITH_RELATED_OBJECTS}
          }
        }
      }
    }
  }
`;

export const FETCH_HOUSEHOLD_WITH_USER = gql`
  query fetchHouseholdWithUser($clientGroupId: SafeString!) {
    fetchClientGroup(input: { clientGroupId: $clientGroupId }) {
      clientGroup {
        id: stringId
        primaryUser {
          ${USER_WITH_RELATED_OBJECTS}
        }
      }
    }
  }
`;

export const FETCH_ACCOUNT_GROUP_WITH_USER = gql`
  query fetchAccountGroupWithUser($accountGroupId: ObjectID!) {
    fetchAccountGroup(accountGroupId: $accountGroupId) {
      accountGroup {
        id
        type
        organization {
          id
          displayCurrency
          availableFeatureFlags
        }
        user {
          ${USER_WITH_RELATED_OBJECTS}
        }
        householdClientGroup {
          id: stringId
          primaryUser {
            ${USER_WITH_RELATED_OBJECTS}
          }
        }
      }
    }
  }
`;
